import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminInput as Input,
  DuviSharedAdminButton as Button,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminAvatar as Avatar,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminStatusPill as StatusPill,
  DuviSharedAdminTooltip as Tooltip,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminMultiSelect as MultiSelect,
  DuviCoreAdminTable,
  DuviCoreAdminTableSkeleton as TableSkeleton,
  DuviCoreAdminEmptyState as EmptyState,
} from "../../components/duvi-core-admin-kit";
import type { Ticket } from "../../api/client";
import {
  DUVI_CORE_ADMIN_TICKET_STATUS_OPTIONS,
  DUVI_CORE_ADMIN_TICKET_PRIORITY_OPTIONS,
  DUVI_CORE_ADMIN_TICKET_CATEGORY_OPTIONS,
  duviCoreAdminPriorityColor,
  duviCoreAdminPriorityLabel,
  duviCoreAdminTicketStatusTone,
  duviCoreAdminTicketStatusLabel,
  duviCoreAdminRelativeTime,
} from "./use-duvi-core-admin-support-tickets-list";

type TicketRow = Ticket & Record<string, unknown>;

export interface DuviCoreAdminSupportTicketsListViewProps {
  tickets: Ticket[];
  isLoading: boolean;
  perPage: number;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (updater: (p: number) => number) => void;
  selectedStatuses: string[];
  setSelectedStatuses: (v: string[]) => void;
  selectedPriorities: string[];
  setSelectedPriorities: (v: string[]) => void;
  selectedCategories: string[];
  setSelectedCategories: (v: string[]) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  slaWarningCount: number;
  openTicket: (id: string) => void;
}

export function DuviCoreAdminSupportTicketsListView(
  props: DuviCoreAdminSupportTicketsListViewProps,
) {
  const {
    tickets,
    isLoading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    selectedStatuses,
    setSelectedStatuses,
    selectedPriorities,
    setSelectedPriorities,
    selectedCategories,
    setSelectedCategories,
    searchQuery,
    setSearchQuery,
    slaWarningCount,
    openTicket,
  } = props;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid={DuviSharedAdminTestIds.ticketsPage}
    >
      {slaWarningCount > 0 && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-md bg-warning-subtle text-warning text-body-sm font-medium">
          <Icon name="alert" size={18} />
          <span>
            <b>{slaWarningCount} tickets</b> approaching SLA — prioritise these
            before they breach.
          </span>
        </div>
      )}

      <Card pad>
        <div className="flex gap-2.5 flex-wrap items-center">
          <MultiSelect
            label="Status"
            testId={DuviSharedAdminTestIds.ticketFilterStatus}
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            options={DUVI_CORE_ADMIN_TICKET_STATUS_OPTIONS}
            width={160}
          />
          <MultiSelect
            label="Priority"
            testId={DuviSharedAdminTestIds.ticketFilterPriority}
            value={selectedPriorities}
            onChange={setSelectedPriorities}
            options={DUVI_CORE_ADMIN_TICKET_PRIORITY_OPTIONS}
            width={140}
          />
          <MultiSelect
            label="Category"
            value={selectedCategories}
            onChange={setSelectedCategories}
            options={DUVI_CORE_ADMIN_TICKET_CATEGORY_OPTIONS}
            width={170}
          />
          <div className="ml-auto max-w-[220px] w-full">
            <Input
              icon={<Icon name="search" size={16} />}
              placeholder="Search tickets"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <TableSkeleton rows={7} cols={6} />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={<Icon name="inbox" size={22} />}
            title="No tickets found"
            desc="No tickets match your current filters."
            testId={DuviSharedAdminTestIds.ticketEmpty}
          />
        ) : (
          <DuviCoreAdminTable<TicketRow>
            rowKey="ticket_id"
            rowTestId={DuviSharedAdminTestIds.ticketRow}
            onRowClick={(r) => openTicket(r.ticket_id)}
            data={tickets as TicketRow[]}
            columns={[
              {
                key: "priority",
                header: "Priority",
                width: 100,
                render: (r) => (
                  <Tooltip
                    content={`${duviCoreAdminPriorityLabel(r.priority)} priority`}
                  >
                    <span className="inline-flex items-center gap-[7px]">
                      <span
                        className="w-[9px] h-[9px] rounded-full flex-none"
                        style={{
                          background: duviCoreAdminPriorityColor(r.priority),
                        }}
                      />
                      <span className="text-caption text-text-muted">
                        {duviCoreAdminPriorityLabel(r.priority)}
                      </span>
                    </span>
                  </Tooltip>
                ),
              },
              {
                key: "ticket_id",
                header: "Ticket",
                width: 120,
                render: (r) => (
                  <a
                    href={`/support/tickets/${r.ticket_id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      openTicket(r.ticket_id);
                    }}
                    className="font-mono text-[12.5px] text-primary"
                  >
                    {r.ticket_id}
                  </a>
                ),
              },
              {
                key: "subject",
                header: "Subject",
                render: (r) => <span className="font-medium">{r.subject}</span>,
              },
              {
                key: "submitter",
                header: "Submitter",
                render: (r) => (
                  <span className="flex items-center gap-2">
                    <Avatar name={r.submitter_id} size="xs" />
                    <span>
                      <div className="text-[13px]">{r.submitter_id}</div>
                      <div className="text-caption text-text-muted">
                        {r.submitter_role}
                      </div>
                    </span>
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (r) => (
                  <StatusPill status={duviCoreAdminTicketStatusTone(r.status)}>
                    {duviCoreAdminTicketStatusLabel(r.status)}
                  </StatusPill>
                ),
              },
              {
                key: "age",
                header: "Age",
                width: 120,
                render: (r) => (
                  <span className="text-text-muted">
                    {duviCoreAdminRelativeTime(r.created_at)}
                  </span>
                ),
              },
              {
                key: "lastReply",
                header: "Last reply",
                width: 130,
                render: (r) => (
                  <span className="text-text-muted">
                    {duviCoreAdminRelativeTime(r.updated_at)}
                  </span>
                ),
              },
            ]}
          />
        )}

        {!isLoading && tickets.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-border">
            <span
              className="text-body-sm text-text-muted"
              data-testid={DuviSharedAdminTestIds.ticketPaginationInfo}
            >
              Showing {Math.min((currentPage - 1) * perPage + 1, totalCount)}–
              {Math.min(currentPage * perPage, totalCount)} of {totalCount}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Icon name="chevronLeft" size={15} />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                data-testid={DuviSharedAdminTestIds.ticketPaginationPrev}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                rightIcon={<Icon name="chevronRight" size={15} />}
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                data-testid={DuviSharedAdminTestIds.ticketPaginationNext}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
